import {
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ChevronRightIcon,
} from '@heroicons/react/outline';
import useScrollableTabs from 'hooks/useScrollableTabs';
import React, { Fragment, useEffect } from 'react';
import { cn } from '@ui/atoms/utils/utils';

const ScrollableTabs = ({
    children = [],
    onTabSelect = null,
    extraComponentAtEnd = null,
    className,
}) => {
    const {
        activeTab,
        elementRef,
        indicatorRef,
        tabCached,
        tabContentRef,
        tabsVisibleOnScreen,
        scrollToHashElement,
        setTabsLoaded,
        setTabClicked,
        switchTab,
    } = useScrollableTabs({
        children,
        onTabSelect,
    });

    const TabNav = ({ label, tabref, onTabSelect, id, gtmtag }) => (
        <div
            key={label}
            onClick={() => {
                setTabClicked(true);
                switchTab(tabref, true);
                scrollToHashElement(id);
            }}
            className={cn(
                'flex text-gray-500 p4-regular lg:bg-gray-50 items-center relative flex-1 lg:flex-row flex-col justify-between bg-basicWhite md:px-10 lg:p-6 p-4 cursor-pointer',
                {
                    'text-primary-500 p4-semibold lg:bg-basicWhite ':
                        activeTab === tabref,
                }
            )}
            ref={activeTab === tabref ? indicatorRef : null}
        >
            {label ? <p
                className="w-full lg:w-4/5 whitespace-nowrap lg:whitespace-normal"
                gtmtag={gtmtag}
            >
                {label}
            </p> : null}
            <div
                className={cn(
                    'lg:block hidden tabs-inactive-left-slice-indicator',
                    {
                        'tabs-active-left-slice-indicator':
                            activeTab === tabref,
                    }
                )}
            >
                <ChevronRightIcon className="w-5 h-5" />
            </div>
            <div
                className={cn(
                    'absolute transition-all ease-in left-0 right-0 h-[2px] lg:hidden -bottom-0',
                    {
                        'bg-primary-500': activeTab === tabref,
                    }
                )}
            />
        </div>
    );

    // Logic of tab content loading
    const isFetchChildren = (tabref) => {
        if (tabCached?.includes(tabref)) return false;

        if (activeTab === tabref) return true;

        if (tabsVisibleOnScreen?.includes(tabref)) return true;

        return false;
    };

    // loading tab data (if more than 1 tabContent are visible on the screen), written this case for line no 68
    useEffect(() => {
        let tabsLoadedOnScreen = [];
        tabsVisibleOnScreen.forEach((element) => {
            if (!tabCached.includes(element)) {
                tabsLoadedOnScreen.push(element);
            }
        });

        setTabsLoaded([...tabCached, ...tabsLoadedOnScreen]);
    }, [tabsVisibleOnScreen]);

    return (
        <div className="w-full flex lg:flex-row flex-col static lg:bg-gray-50">
            <div
                className={cn(
                    `sticky top-16 scrollbar-hide z-20 w-full lg:w-1/5 bg-basicWhite lg:bg-gray-50 overflow-auto scroll-horizontal-width-0 flex lg:flex-col top-header-mobile md:top-header h-full max-h-[65vh] slide-${activeTab} lg:scroll-vertical-width-25px lg:shadow-none shadow-xs`,
                    className
                )}
                ref={elementRef}
            >
                {activeTab !== children[0]?.props?.tabref ? (
                    <div
                        className="md:hidden bottom-0 left-0 sticky z-50 flex items-center bg-basicWhite opacity-9.5"
                        onClick={() => {
                            elementRef.current.scrollLeft += -100;
                        }}
                    >
                        <ChevronDoubleLeftIcon className="w-5 h-5 opacity-70 text-primary-500" />
                    </div>
                ) : null}
                {children.map((element, i) => {
                    return element && element?.props?.label && (
                            <Fragment key={`tabnavkey-${i}`}>
                                <TabNav
                                    label={element?.props?.label}
                                    tabref={element?.props?.tabref}
                                    id={element?.props?.id}
                                    gtmtag={element?.props?.gtmtag}
                                    index={i}
                                />
                            </Fragment>
                        )
                })}
                {activeTab !== children[children?.length - 1]?.props?.tabref ? (
                    <div
                        className="md:hidden bottom-0 right-0 sticky z-50 flex items-center bg-basicWhite opacity-9.5 pl-2"
                        onClick={() => {
                            elementRef.current.scrollLeft += 100;
                        }}
                    >
                        <ChevronDoubleRightIcon className="w-5 h-5 opacity-70 text-primary-500" />
                    </div>
                ) : null}
            </div>
            <div className="w-full lg:w-4/5 flex flex-col px-4 md:px-6 xl:px-10 bg-basicWhite"
                ref={tabContentRef}
            >
                {children.map((element, i) => {
                    return element && (
                            <div
                                key={`tabbodykey-${i}`}
                                className="w-full overflow-auto tab-content-wrapper"
                                id={element?.props?.id}
                                data-tabref={element?.props?.tabref}
                            >
                                {/*  tab content with isFetching prop */}
                                {React.cloneElement(element?.props?.children, {
                                    isFetching: isFetchChildren(
                                        element?.props?.tabref
                                    ),
                                    dataPresent: tabCached.includes(
                                        element?.props?.tabref
                                    ),
                                })}
                            </div>
                        )
                })}

                {extraComponentAtEnd}
            </div>
        </div>
    );
};

export default ScrollableTabs;
